/**
 * The long-form Persian game guides shown on the Guides (راهنما) screen.
 *
 * Content lives here — NOT in `i18n/fa.ts` — because these are multi-paragraph
 * rule texts, not UI strings. THE PURE ENGINES ARE THE SOURCE OF TRUTH: every
 * rule below matches the variant implemented in
 * `packages/engines/{hokm,hearts,shelem,pasur,bluff,chess,backgammon,mensch,ladders,dooz,sudoku}`
 * (e.g. Hokm's کوت is always ۲ points — there is no 3-point حاکم‌کوت here;
 * Pasur is two sides playing to a target (۶۲, or a chosen ۱۰۰ or ۱۰۴) with a
 * سور cap 12 below it, and its Jack takes every number card and
 * every Jack on the table, never a Queen or King; منچ's capture is OPTIONAL and
 * its start square is NOT safe, because `DEFAULT_MENSCH_CONFIG` sets
 * `mandatoryCapture:false` and `safeStartSquare:false`; تخته نرد has NO doubling
 * cube in v1).
 * Keep this file in sync with the engines when a rule changes.
 */

import { LOBBY_GAME_ORDER } from "@bing/shared";

export type GuideGameId =
  | "hokm"
  | "hearts"
  | "shelem"
  | "pasur"
  | "bluff"
  | "chess"
  | "mensch"
  | "ladders"
  | "backgammon"
  | "dooz"
  | "snake"
  | "sudoku";

/** The five fixed section headings, in display order — identical for every game. */
export const GUIDE_SECTION_TITLES = {
  goal: "هدف بازی",
  players: "تعداد بازیکن‌ها",
  howToPlay: "روش بازی",
  keyRules: "قوانین کلیدی",
  scoring: "امتیازشماری",
} as const;

export interface GameGuide {
  readonly id: GuideGameId;
  /** Persian display name (matches the lobby tile). */
  readonly name: string;
  /** One-line teaser shown on the collapsed accordion header. */
  readonly summary: string;
  /** هدف بازی */
  readonly goal: string;
  /** تعداد بازیکن‌ها */
  readonly players: string;
  /** روش بازی — ordered, numbered steps. */
  readonly howToPlay: readonly string[];
  /** قوانین کلیدی — bullet points. */
  readonly keyRules: readonly string[];
  /** امتیازشماری — bullet points. */
  readonly scoring: readonly string[];
}

/** Every guide, in no particular order — {@link GAME_GUIDES} is the list as it is shown. */
const GUIDES: readonly GameGuide[] = [
  {
    id: "hokm",
    name: "حکم",
    summary: "بازی تیمیِ چهارنفره با خالِ حکم؛ تیمی که زودتر ۷ دور ببرد، دست را می‌برد.",
    goal: "حکم محبوب‌ترین بازی کارتی ایران است: دو تیم بر سرِ بردنِ دورها رقابت می‌کنند. در هر دست، تیمی که زودتر ۷ دور از ۱۳ دور را ببرد برندهٔ آن دست می‌شود و امتیاز می‌گیرد. تیمی که زودتر به ۷ امتیاز برسد، برندهٔ کلِ بازی است.",
    players:
      "۴ نفر در دو تیمِ دونفره. هم‌تیمی‌ها روبه‌روی هم می‌نشینند و دورهایی که می‌برند برای تیمشان حساب می‌شود.",
    howToPlay: [
      "در آغازِ بازی، کارت‌ها یکی‌یکی روباز پخش می‌شوند؛ نخستین کسی که آس بگیرد «حاکم» می‌شود.",
      "حاکم ابتدا فقط ۵ کارتِ اولِ خودش را می‌بیند و بر اساسِ همان‌ها یکی از چهار خال را به‌عنوان «حکم» (آتو) اعلام می‌کند.",
      "سپس بقیهٔ کارت‌ها پخش می‌شود تا هر بازیکن ۱۳ کارت داشته باشد؛ حاکم نخستین کارت را بازی می‌کند.",
      "بازیکنان به‌نوبت یک کارت می‌اندازند: اگر از خالِ کارتِ اولِ زمین (خالِ زمینه) دارید، باید همان خال را بازی کنید.",
      "اگر از خالِ زمینه ندارید، آزادید: می‌توانید حکم بزنید (ببُرید) یا هر کارتِ دیگری خالی بدهید.",
      "برندهٔ هر دور: اگر حکمی بازی شده باشد بالاترین حکم، وگرنه بالاترین کارتِ خالِ زمینه. برندهٔ هر دور، شروع‌کنندهٔ دورِ بعد است.",
      "به‌محضِ اینکه تیمی ۷ دور ببرد، دست تمام می‌شود، امتیازش ثبت می‌شود و دستِ بعد با پخشِ دوباره آغاز می‌شود.",
    ],
    keyRules: [
      "پیروی از خال اجباری است: تا وقتی از خالِ زمینه کارت دارید، نمی‌توانید کارتِ دیگری بازی کنید.",
      "زدنِ حکم اجباری نیست؛ وقتی خال ندارید می‌توانید به‌جای بریدن، کارتِ کم‌ارزشی خالی بدهید.",
      "کوچک‌ترین حکم هم از بلندترین کارتِ غیرحکم بالاتر است؛ میانِ چند حکم، رتبهٔ بالاتر می‌برد.",
      "ترتیبِ رتبه‌ها از بالا به پایین: آس، شاه، بی‌بی، سرباز، ۱۰، ۹، … ، ۲.",
      "اگر تیمِ حاکم دست را ببرد، حاکمی نزدِ همان بازیکن می‌ماند؛ اگر ببازد، حاکمی به نفرِ بعدی — که از تیمِ برنده است — می‌رسد و او حکمِ دستِ بعد را انتخاب می‌کند.",
    ],
    scoring: [
      "بردِ معمولیِ هر دست (۷ دور): ۱ امتیاز برای تیمِ برنده.",
      "کوت: اگر تیمِ بازنده هیچ دوری نگرفته باشد (۷ بر صفر)، تیمِ برنده ۲ امتیاز می‌گیرد.",
      "نخستین تیمی که به ۷ امتیاز برسد، برندهٔ بازی است.",
    ],
  },
  {
    id: "hearts",
    name: "بی‌دل",
    summary: "بازی انفرادیِ چهارنفره؛ از دل‌ها و بی‌بیِ پیک فرار کنید — امتیازِ کمتر یعنی برد.",
    goal: "بی‌دل برعکسِ بیشترِ بازی‌هاست: امتیاز در آن جریمه است! هر دل ۱ امتیاز و بی‌بیِ پیک ۱۳ امتیاز جریمه دارد؛ باید طوری بازی کنید که این کارت‌ها نصیبتان نشود. وقتی امتیازِ یک نفر به ۱۰۰ برسد بازی تمام می‌شود و کم‌امتیازترین بازیکن برنده است.",
    players: "۴ نفر و هرکس برای خودش؛ تیمی در کار نیست. به هر بازیکن ۱۳ کارت می‌رسد.",
    howToPlay: [
      "پیش از شروعِ هر دست، هر بازیکن ۳ کارت از دستش را انتخاب و ردوبدل می‌کند: دستِ نخست به نفرِ راست، دستِ دوم به چپ، دستِ سوم به روبه‌رو و دستِ چهارم بدونِ ردوبدل؛ سپس همین چرخه تکرار می‌شود.",
      "دارندهٔ ۲ خاج بازی را شروع می‌کند و باید همان ۲ خاج را بیندازد.",
      "بازیکنان به‌نوبت کارت می‌اندازند و اگر از خالِ زمینه دارند، باید همان خال را بازی کنند.",
      "این بازی حکم (آتو) ندارد: بالاترین کارتِ خالِ زمینه دور را می‌برد و کارت‌های آن — با همهٔ جریمه‌هایش — به برنده می‌رسد؛ او دورِ بعد را شروع می‌کند.",
      "وقتی هر ۱۳ دور بازی شد، جریمه‌های هرکس به جمعِ امتیازش اضافه می‌شود و دستِ بعد پخش می‌شود.",
    ],
    keyRules: [
      "در دورِ اول کسی حق ندارد دل یا بی‌بیِ پیک بیندازد — مگر اینکه کارتِ دیگری نداشته باشد.",
      "تا وقتی «دل نشکسته»، نمی‌توانید دوری را با دل شروع کنید. دل وقتی می‌شکند که برای نخستین بار کسی دلی بازی کند — یعنی وقتی خالِ زمینه را ندارد و دلی خالی می‌دهد. بی‌بیِ پیک دل را نمی‌شکند.",
      "استثنا: اگر همهٔ کارت‌هایتان دل باشد، می‌توانید با دل شروع کنید.",
      "وقتی خالِ زمینه را ندارید، هر کارتی — از جمله دل یا بی‌بیِ پیک — می‌توانید خالی بدهید؛ بهترین فرصت برای خلاص‌شدن از کارت‌های خطرناک!",
    ],
    scoring: [
      "هر دل: ۱ امتیازِ جریمه؛ بی‌بیِ پیک: ۱۳ امتیاز. مجموعِ جریمه‌های هر دست ۲۶ امتیاز است.",
      "شوت: اگر یک نفر همهٔ ۲۶ امتیازِ یک دست (هر ۱۳ دل و بی‌بیِ پیک) را یکجا جمع کند، خودش صفر می‌گیرد و هر سه حریفش ۲۶ امتیاز جریمه می‌شوند.",
      "به‌محضِ اینکه امتیازِ کسی به ۱۰۰ برسد، بازی تمام است؛ بازیکنی که کمترین امتیاز را دارد، برنده است.",
    ],
  },
  {
    id: "shelem",
    name: "شلم",
    summary:
      "بازی تیمی با مزایده و ۴ کارتِ «زمین»؛ هر دست ۱۶۵ امتیاز دارد — ادعا کنید و اثبات کنید.",
    goal: "شلم بازیِ ادعا و اثبات است: در مزایده اعلام می‌کنید تیمتان چند امتیاز از ۱۶۵ امتیازِ دست را جمع می‌کند و بعد باید در بازی به آن برسید — وگرنه جریمه می‌شوید. بازی تا ۶۶۰ امتیاز است. در میزِ خصوصی میزبان، و در بازیِ تک‌نفره خودتان، می‌توانید ۱۲۰۰ را انتخاب کنید؛ بازیِ آنلاینِ عمومی همیشه تا ۶۶۰ است.",
    players:
      "۴ نفر در دو تیمِ دونفره؛ هم‌تیمی‌ها روبه‌روی هم. با یک دستِ ۵۲ کارتی (بدونِ ژوکر) بازی می‌شود: به هر بازیکن ۱۲ کارت می‌رسد و ۴ کارت روبه‌پایین وسط می‌ماند که «زمین» (کف) نام دارد.",
    howToPlay: [
      "مزایده از نفرِ سمتِ راستِ کارت‌دهنده شروع می‌شود و به‌نوبت به راست می‌چرخد: هرکس یا رقمی بالاتر از رقمِ قبلی می‌خواند یا پاس می‌دهد. رقم‌ها مضربِ ۵ و از ۱۰۰ تا ۱۶۵ هستند و پاس قطعی است: کسی که پاس داد، در آن دست دیگر نمی‌خواند.",
      "به‌جای رقم می‌توانید «شلم» بخوانید — یعنی قول می‌دهید تیمتان هر ۱۲ دور را ببرد. شلم از ۱۶۵ هم بالاتر است، پس مزایده همان‌جا تمام می‌شود و خواننده‌اش حاکم است.",
      "اگر سه نفرِ اول پاس بدهند و کسی رقمی نخوانده باشد، کارت‌ها جمع و دوباره پخش می‌شود و کارت‌دادن به نفرِ بعد می‌رسد؛ از نفرِ چهارم پرسیده نمی‌شود.",
      "بالاترین خواننده «حاکم» می‌شود: ۴ کارتِ زمین را برمی‌دارد (۱۶ کارته می‌شود) و ۴ کارتِ دلخواهش را روبه‌پایین کنار می‌گذارد؛ این ۴ کارت تا پایانِ دست پنهان می‌ماند.",
      "حاکم نخستین کارت را بازی می‌کند و خالِ همان کارت «حکم» می‌شود — اعلامِ جداگانه‌ای در کار نیست.",
      "۱۲ دور بازی می‌شود: پیروی از خالِ زمینه اجباری است؛ اگر خال ندارید، می‌توانید حکم بزنید یا خالی بدهید. برندهٔ هر دور (بالاترین حکم، وگرنه بالاترین کارتِ خالِ زمینه؛ آس از همه بالاتر است) کارت‌ها را برای تیمش جمع می‌کند و دورِ بعد را شروع می‌کند.",
      "در پایانِ ۱۲ دور امتیازها شمرده و ثبت می‌شود؛ سپس کارت‌دهنده یک نفر به راست می‌چرخد و دستِ بعد پخش می‌شود.",
    ],
    keyRules: [
      "کارت‌های امتیازدار: هر آس ۱۰، هر ده‌لو ۱۰ و هر پنج‌لو ۵ امتیاز (جمعاً ۱۰۰). هر دورِ برده ۵ امتیاز دارد و زمین هم یک دورِ بردهٔ تیمِ حاکم حساب می‌شود (۱۳ × ۵ = ۶۵). پس کلِ هر دست ۱۶۵ امتیاز است.",
      "امتیازِ ۴ کارتی که حاکم کنار می‌گذارد، به‌علاوهٔ ۵ امتیاز، آخرِ دست برای تیمِ حاکم حساب می‌شود — پس زمین را هوشمندانه عوض کنید.",
      "تیمِ مدافع هرچه در بازی جمع کند برای خودش نگه می‌دارد — جز وقتی که تیمِ حاکم هر ۱۲ دور را ببرد.",
    ],
    scoring: [
      "اگر تیمِ حاکم دست‌کم به‌اندازهٔ رقمِ خوانده‌شده امتیاز جمع کند: همهٔ امتیازی که جمع کرده به حسابش اضافه می‌شود.",
      "اگر کم بیاورد: فقط به‌اندازهٔ رقمِ خوانده‌شده از امتیازش کم می‌شود — حتی اگر از تیمِ مدافع هم کمتر جمع کرده باشد. امتیازِ تیم می‌تواند منفی شود.",
      "اگر تیمِ حاکم بی‌آنکه شلم خوانده باشد هر ۱۲ دور را ببرد، ۱۶۵ امتیاز می‌گیرد و تیمِ مدافع صفر.",
      "اگر تیمِ مدافع هر ۱۲ دور را ببرد، فقط همان امتیازی را می‌گیرد که جمع کرده — زمین همیشه مالِ تیمِ حاکم است.",
      "شلمِ خوانده‌شده: اگر تیمِ حاکم هر ۱۲ دور را ببرد ۳۳۰ امتیاز می‌گیرد و تیمِ مدافع صفر؛ اگر حتی یک دور را ببازد، ۳۳۰ امتیاز از او کم می‌شود و تیمِ مدافع امتیازِ خودش را می‌برد.",
      "پس از هر دست، اگر تیمی به هدفِ بازی (۶۶۰، یا ۱۲۰۰ اگر انتخاب شده) رسیده باشد، بازی تمام است: امتیازِ بیشتر می‌برد و در تساویِ کامل، تیمی که حاکمِ دستِ آخر بود برنده است.",
    ],
  },
  {
    id: "pasur",
    name: "پاسور",
    summary:
      "بازیِ تیمیِ جمعِ یازده: با یارِ روبه‌رو کارت‌های زمین را شکار کنید؛ پس از هر دست، تیمِ بالاتر با ۶۲ امتیاز یا بیشتر می‌برد (اگر هدفِ بالاتری انتخاب نشده باشد).",
    goal: "پاسور (چهاربرگ) بازیِ دو تیمِ دونفره است: با هر کارتی که بازی می‌کنید می‌کوشید کارت‌هایی از زمین «جمع» کنید. در پایانِ هر دست، کارت‌های امتیازدار و سورهای هر تیم شمرده می‌شود و امتیازِ دست‌ها روی هم جمع می‌شود؛ پس از هر دست، تیمِ بالاتر با ۶۲ امتیاز یا بیشتر برنده است؛ برابری در صدر، دستِ دیگری می‌خواهد. هدفِ بازی در میزهای عمومی همیشه ۶۲ است؛ در بازیِ تک‌نفره انتخاب با خودتان است و در میزِ خصوصی با میزبان: ۶۲، ۱۰۰ یا ۱۰۴.",
    players:
      "۴ نفر در دو تیمِ دونفره. یارها روبه‌روی هم می‌نشینند و کارت‌ها، سورها و امتیازشان با هم حساب می‌شود. دونفره هم بازی می‌شود: یک به یک، رو در رو.",
    howToPlay: [
      "در آغازِ هر دست به هر بازیکن ۴ کارت و روی زمین هم ۴ کارتِ روباز داده می‌شود؛ بقیهٔ کارت‌ها دسته می‌ماند. نفرِ بعد از کارت‌دهنده بازی را شروع می‌کند.",
      "اگر میانِ ۴ کارتِ زمین یک سرباز بیاید، آن سرباز لای دسته (در جایی نامعلوم) برمی‌گردد و کارتی دیگر جایش را می‌گیرد؛ اگر دست‌کم دو سرباز، یا دست‌کم سه بی‌بی یا سه شاه بیاید، کارت‌ها دوباره بُر می‌خورند.",
      "در نوبتتان یک کارت بازی می‌کنید. کارتِ عددی (آس = ۱ و ۲ تا ۱۰ به ارزشِ خودشان) یک دسته از کارت‌های عددیِ زمین را که با آن جمعاً ۱۱ شود جمع می‌کند — یک کارت یا چند کارت با هم؛ مثلاً با ۴ یک ۷ را، یا با آس ترکیبِ ۶ و ۴ را.",
      "سرباز همهٔ کارت‌های عددی و سربازهای روی زمین را یکجا جمع می‌کند، ولی هرگز بی‌بی و شاه را؛ اگر چیزی برای جمع‌کردن نباشد، روی زمین می‌ماند.",
      "بی‌بی فقط یک بی‌بی و شاه فقط یک شاه را از زمین جمع می‌کند.",
      "کارتِ بازی‌شده و هرچه جمع کرده به کپهٔ تیمتان می‌رود؛ کارتی که جمعی ندارد، روی زمین می‌ماند.",
      "وقتی دستِ همه خالی شد، به هرکس ۴ کارتِ تازه از دسته داده می‌شود (به زمین کارتی اضافه نمی‌شود) تا دسته تمام شود.",
      "پس از آخرین کارتِ دست، باقی‌ماندهٔ زمین به آخرین کسی می‌رسد که جمع کرده است؛ سپس امتیازِ دست شمرده می‌شود و کارت‌دهنده به نفرِ بعدی می‌رسد.",
    ],
    keyRules: [
      "هر بار فقط یک دسته جمع می‌کنید؛ اگر چند دستهٔ ممکن باشد، انتخاب با شماست.",
      "جمع‌کردن اجباری است: کارتی که می‌تواند جمع کند، نمی‌توان خالی روی زمین گذاشت — ولی می‌توانید به‌جایش کارتِ دیگری بازی کنید که جمعی ندارد.",
      "سور: اگر با یک جمع، زمین را کاملاً خالی کنید، سور زده‌اید و ۵ امتیاز دارد.",
      "خالی‌کردنِ زمین با سرباز سور نیست، و در دورِ آخرِ هر دست (وقتی دسته تمام شده) هیچ سوری زده نمی‌شود.",
      "اگر تیمِ حریف سور داشته باشد، سورِ شما یکی از سورهای حریف را خنثی می‌کند و برای شما حساب نمی‌شود. سورها در هر دست جداگانه شمرده می‌شوند.",
      "سقفِ سور: بازیکن یا تیمی که امتیازش در آخرین شمارش به سقف رسیده یا از آن گذشته باشد، نه سور می‌زند و نه سورِ حریف را خنثی می‌کند؛ خالی‌کردنِ زمین برایش یک جمعِ معمولی است و سورِ حریف سرِ جایش می‌ماند.",
      "آخرین شمارش یعنی امتیازی که تا پایانِ دستِ قبل جمع شده: کارت‌هایی که در همین دست جمع کرده‌اید حساب نمی‌شوند، و در بازیِ چهارنفره امتیازِ دو یار با هم حساب می‌شود.",
      "سقف همیشه ۱۲ امتیاز پایین‌تر از هدفِ بازی است: هدفِ ۶۲ سقفِ ۵۰ دارد، هدفِ ۱۰۰ سقفِ ۸۸ و هدفِ ۱۰۴ سقفِ ۹۲. میزهای عمومی همیشه تا ۶۲ بازی می‌شوند؛ در بازیِ تک‌نفره انتخاب با خودتان است و در میزِ خصوصی با میزبان: ۶۲ (پیش‌فرض)، ۱۰۰ یا ۱۰۴.",
      "رسیدنِ باقی‌ماندهٔ زمین به آخرین جمع‌کننده، سور حساب نمی‌شود.",
    ],
    scoring: [
      "هفت‌خاج — ۷ خاج یا بیشتر: ۷ امتیاز.",
      "۱۰ خشت: ۳ امتیاز؛ ۲ خاج: ۲ امتیاز؛ هر آس: ۱ امتیاز؛ هر سرباز: ۱ امتیاز.",
      "پس کارت‌ها در هر دست روی هم ۲۰ امتیاز دارند؛ هر سور هم ۵ امتیاز.",
      "امتیازِ دست‌ها روی هم جمع می‌شود. پس از هر دست، اگر بیشترین امتیاز ۶۲ یا بیشتر باشد، آن تیم برنده است — حتی اگر هر دو تیم در همان دست از ۶۲ گذشته باشند، تیمِ بالاتر می‌برد؛ اگر دو تیم در صدر مساوی باشند، دستِ دیگری بازی می‌شود. اگر هدفِ ۱۰۰ یا ۱۰۴ انتخاب شده باشد، همین قاعده با همان عدد حساب می‌شود.",
    ],
  },
  {
    id: "bluff",
    name: "بلوف",
    summary: "کارت‌ها را پنهانی بریزید و ادعا کنید؛ راست یا دروغ — زودتر دستتان را خالی کنید!",
    goal: "هدف این است که زودتر از همه دستتان را خالی کنید. کارت‌ها روبه‌پایین بازی می‌شوند و شما فقط «ادعا» می‌کنید چه ریخته‌اید — لازم نیست راست بگویید! حریفان هم می‌توانند ادعایتان را به چالش بکشند؛ هنرِ بازی، دروغِ به‌موقع و شکارِ دروغِ دیگران است.",
    players:
      "۳ تا ۶ نفر و هرکس برای خودش؛ در این اپ چهارنفره بازی می‌شود. کلِ ۵۲ کارت تا حدِ ممکن مساوی پخش می‌شود.",
    howToPlay: [
      "رتبهٔ اعلامی از آس شروع می‌شود و با هر نوبت یک پله جلو می‌رود: آس، ۲، ۳، … تا شاه و دوباره آس.",
      "در نوبتتان باید ۱ تا ۴ کارت روبه‌پایین وسط بگذارید و ادعا کنید همه از رتبهٔ همان نوبت‌اند — چه واقعاً باشند، چه نباشند.",
      "پس از هر ریختن، بازیکنانِ دیگر به‌نوبت — از نفرِ کنارِ ریزنده — یا «بلوف!» می‌گویند یا رد می‌شوند.",
      "اگر همه رد شوند، ریختن پذیرفته می‌شود و نوبت با رتبهٔ بعدی به نفرِ بعد می‌رسد.",
      "اگر کسی بلوف بگوید، فقط کارت‌های همان ریختنِ آخر رو می‌شوند: اگر حتی یک کارت با ادعا نخوانَد، ریزنده همهٔ کپه را برمی‌دارد؛ اگر ادعا راست بوده باشد، چالش‌کننده کپه را برمی‌دارد.",
      "پس از هر برداشتنِ کپه، رتبه دوباره از آس شروع می‌شود و نوبت به نفرِ کنارِ بردارندهٔ کپه می‌رسد.",
    ],
    keyRules: [
      "رتبهٔ ادعا انتخابی نیست: هر نوبت باید همان رتبهٔ جاری را اعلام کنید؛ انتخابِ شما فقط تعداد و خودِ کارت‌هاست.",
      "حتی اگر از رتبهٔ جاری هیچ کارتی ندارید، باز هم باید بریزید — یعنی گاهی بلوف‌زدن اجباری است!",
      "در هر چالش فقط ریختنِ آخر رو می‌شود، اما بازنده کلِ کپه را برمی‌دارد.",
      "اگر آخرین کارت‌هایتان را صادقانه بریزید و کسی به‌اشتباه بلوف بگوید، باز هم برنده‌اید — کپه به چالش‌کنندهٔ اشتباه می‌رسد.",
      "اگر آخرین کارت‌هایتان بلوف باشد و لو بروید، کپه به دستتان برمی‌گردد و بازی ادامه می‌یابد.",
    ],
    scoring: [
      "این بازی امتیازِ عددی ندارد: نخستین کسی که همهٔ کارت‌هایش را بریزد و ریختنِ آخرش پابرجا بماند — یا در چالش صادق از آب دربیاید — همان لحظه برندهٔ بازی است.",
    ],
  },
  {
    id: "chess",
    name: "شطرنج",
    summary: "نبردِ دو ذهن روی ۶۴ خانه؛ شاهِ حریف را مات کن تا ببری.",
    goal: "شطرنج بازیِ دونفرهٔ کلاسیک روی صفحه‌ای ۸×۸ است. هدف «مات» کردنِ شاهِ حریف است: شاهی که کیش شده و هیچ حرکتِ قانونی برای فرار از کیش ندارد. اگر هیچ‌کدام نتوانید مات کنید، بازی مساوی می‌شود.",
    players:
      "۲ نفر. سفید همیشه اولین حرکت را انجام می‌دهد؛ در بازیِ تک‌نفره پیش از شروع انتخاب می‌کنید سفید باشید یا سیاه، و سطحِ حریف را هم همان‌جا تعیین می‌کنید.",
    howToPlay: [
      "هر بازیکن ۱۶ مهره دارد: یک شاه، یک وزیر، دو رخ، دو فیل، دو اسب و هشت سرباز.",
      "سفید شروع می‌کند و از آن پس به‌نوبت، هر بار یک مهره حرکت می‌دهید.",
      "رخ افقی و عمودی می‌رود، فیل اریب، وزیر هر دو، و شاه یک خانه در هر جهت.",
      "اسب به شکلِ «ال» حرکت می‌کند و تنها مهره‌ای است که از روی مهره‌های دیگر می‌پرد.",
      "سرباز یک خانه به جلو می‌رود — از خانهٔ اولش می‌تواند دو خانه — ولی فقط اریب و یک خانه می‌زند.",
      "برای زدن، مهره‌تان را روی خانهٔ مهرهٔ حریف بگذارید؛ آن مهره از صفحه بیرون می‌رود و در سینیِ کنارِ صفحه دیده می‌شود.",
      "اگر شاهتان کیش شود، حرکتِ بعدی‌تان حتماً باید کیش را رفع کند — بردنِ مهرهٔ مهاجم، جلوی آن سد شدن، یا فرارِ شاه.",
      "به‌محضِ اینکه شاهِ حریف مات شود، بازی همان‌جا تمام است.",
    ],
    keyRules: [
      "قلعه: اگر شاه و آن رخ هنوز حرکت نکرده باشند و خانه‌های بینشان خالی باشد، شاه دو خانه به‌سمتِ رخ می‌رود و رخ از رویش رد می‌شود. شاه نباید در کیش باشد، از خانهٔ زیرِ ضرب بگذرد، یا در خانهٔ زیرِ ضرب بنشیند.",
      "قلعه روی صفحه: شاه را لمس کنید، بعد خانه‌ای را که دو خانه به‌سمتِ رخ است — یا خودِ رخ را.",
      "آن‌پاسان: اگر سربازِ حریف با حرکتِ دوخانه‌ای از کنارِ سربازِ شما رد شود، فقط بلافاصله در همان نوبت می‌توانید آن را طوری بزنید که انگار یک خانه رفته بود.",
      "ترفیع: سربازی که به آخرین ردیف برسد به وزیر، رخ، فیل یا اسب تبدیل می‌شود — انتخاب با شماست.",
      "هیچ‌وقت نمی‌توانید حرکتی بازی کنید که شاهِ خودتان را در کیش بگذارد.",
      "صفحه فقط حرکت‌های قانونی را پیشنهاد می‌دهد و فهرستِ حرکت‌ها کنارِ صفحه ثبت می‌شود.",
      "میزبانِ میزِ خصوصی می‌تواند زمانِ بازی را انتخاب کند: آزاد (بدون ساعت) یا ۳+۲، ۵+۰، ۱۰+۰ و ۱۵+۱۰. عددِ اول دقیقهٔ کلِ هر بازیکن است و عددِ دوم ثانیه‌ای که پس از هر حرکت به وقتش اضافه می‌شود. میزهای عمومی ۱۰+۰ هستند.",
    ],
    scoring: [
      "مات: همان لحظه برندهٔ بازی هستید.",
      "پات: اگر نوبتِ کسی باشد که کیش نیست ولی هیچ حرکتِ قانونی ندارد، بازی مساوی است.",
      "۵۰ حرکتِ پیاپی بدونِ زدنِ مهره و بدونِ حرکتِ سرباز: مساوی.",
      "تکرارِ سه‌بارهٔ یک وضعیت: مساوی.",
      "مهرهٔ ناکافی برای مات (مثلاً شاهِ تنها، یا شاه و یک فیل/اسب): مساوی.",
      "در میزِ زمان‌دار، هرکس وقتش تمام شود می‌بازد — مگر حریفش هم مهرهٔ کافی برای مات نداشته باشد که در آن صورت مساوی می‌شود.",
    ],
  },
  {
    id: "mensch",
    name: "منچ",
    summary: "با ۶ مهره را از حیاط بیرون بیاور، دورِ صفحه بچرخان و هر چهار مهره را به خانه برسان.",
    goal: "منچ یک مسابقهٔ ساده و پرهیجان است: باید هر چهار مهره‌تان را از حیاط بیرون بیاورید، یک دورِ کاملِ صفحه (۴۰ خانه) بچرخانید و به چهار خانهٔ پایانیِ رنگِ خودتان برسانید. هرکس زودتر هر چهار مهره‌اش را داخل ببرد، برندهٔ بازی است.",
    players:
      "۲ تا ۴ نفر و هرکس برای خودش. با دو نفر، بازیکن‌ها روبه‌روی هم می‌نشینند؛ طولِ مسیر برای همه دقیقاً یکسان است.",
    howToPlay: [
      "هر بازیکن ۴ مهره دارد که در آغازِ بازی همه در حیاطِ خودش هستند.",
      "در نوبتتان یک بار تاس می‌اندازید.",
      "فقط ۶ مهره را از حیاط بیرون می‌آورد؛ آن مهره روی خانهٔ شروعِ رنگِ شما می‌نشیند.",
      "اگر مهره‌ای در مسیر دارید، عددِ تاس را با یکی از آن‌ها بازی می‌کنید — انتخابِ اینکه کدام مهره جلو برود با شماست.",
      "اگر ۶ آوردید و آن ۶ را واقعاً بازی کردید، یک تاسِ دیگر هم می‌اندازید.",
      "اگر هیچ حرکتِ قانونی نداشته باشید، نوبت خودبه‌خود به نفرِ بعد می‌رسد.",
      "پس از یک دورِ کامل، مهره وارد چهار خانهٔ پایانیِ خودتان می‌شود؛ آنجا دیگر هیچ‌کس نمی‌تواند بزندش.",
    ],
    keyRules: [
      "در هر نوبت فقط یک بار تاس می‌اندازید — حتی وقتی همهٔ مهره‌هایتان هنوز در حیاط است. این قانونِ اصلیِ منچ است، پس طبیعی است که اولِ بازی چند نوبت بدونِ ۶ رد شود.",
      "هرگز نمی‌توانید روی مهرهٔ خودتان بنشینید؛ در آن صورت باید مهرهٔ دیگری را حرکت دهید.",
      "اگر روی مهرهٔ حریف بنشینید، آن مهره به حیاطِ خودش برمی‌گردد و باید از نو راه بیفتد.",
      "زدن اجباری نیست: حتی وقتی تاس‌تان زدن را ممکن می‌کند، آزادید مهرهٔ دیگری را جلو ببرید.",
      "خانهٔ شروع امن نیست: مهره‌ای که روی خانهٔ شروع ایستاده هم زده می‌شود.",
      "فقط خانهٔ مقصد اهمیت دارد؛ در راه از روی هر مهره‌ای — خودی یا حریف — رد می‌شوید.",
      "برای رفتن به خانه‌های پایانی عددِ دقیق لازم است: تاسی که مهره را از آخرین خانه ردّ کند اصلاً بازی نمی‌شود.",
    ],
    scoring: [
      "منچ امتیازِ عددی ندارد: نخستین کسی که هر چهار مهره‌اش را به خانه‌های پایانی برساند، همان لحظه برندهٔ بازی است.",
    ],
  },
  /*
   * مار پله (card-86) — `@bing/engine-ladders`'s rules, THE OPERATOR'S (2026-11-03): a roll past ۱۰۰ does
   * not move the piece (no bounce anywhere), only exactly ۱۰۰ wins, and EVERY ۶ earns one more roll — a
   * ۶ that cannot move included, with no cap. ROUND 2 (the operator, same day): a turn is two taps
   * (the die, then your piece; a turn's window plays itself when it lapses), CAPTURE on the final
   * cell (never on ۱۰۰ — so pieces no longer share a cell), three LEVELS and the LOOKS. The seven
   * sentences of the plan's §8.6 are the bot tutorial's too, BYTE FOR BYTE (`apps/bot/src/tutorials.ts`;
   * `ladders-guide.test.ts` pins them) — a wording change is made in all three places at once.
   */
  {
    id: "ladders",
    name: "مار پله",
    summary:
      "تاس بریز، از پله‌ها بالا برو، مراقبِ مارها باش و زودتر از همه دقیقاً به خانهٔ ۱۰۰ برس.",
    goal: "مار پله مسابقهٔ ساده‌ای روی صفحهٔ ۱۰۰ خانه‌ای است: به‌نوبت تاس می‌ریزید و مهره‌تان را جلو می‌برید. پله‌ها مهره را بالا می‌برند و مارها پایین می‌آورند. هرکس زودتر دقیقاً به خانهٔ ۱۰۰ برسد، برندهٔ بازی است.",
    players:
      "۲ یا ۴ نفر و هرکس برای خودش؛ هر بازیکن یک مهره به رنگِ خودش دارد. میزِ عمومی ۲ نفره و ۴ نفره دارد، در میزِ خصوصی میزبان ۲ یا ۴ نفر را انتخاب می‌کند و در بازیِ تک‌نفره هم ۲ یا ۴ نفره با ربات بازی می‌کنی.",
    howToPlay: [
      "همهٔ مهره‌ها بیرونِ صفحه، زیرِ خانهٔ ۱، شروع می‌کنند و نفرِ اول به‌طورِ تصادفی انتخاب می‌شود.",
      "نوبتت که شد، روی تاس بزن تا ریخته شود؛ بعد روی مهره‌ات بزن تا حرکت کند.",
      "مهره‌ات به اندازهٔ عددِ تاس، خانه‌به‌خانه جلو می‌رود.",
      "خانه‌ها از پایینِ صفحه (خانهٔ ۱) ردیف‌به‌ردیف و مارپیچ بالا می‌روند تا خانهٔ ۱۰۰ در بالای صفحه.",
      "اگر حرکتت روی پایینِ یک پله تمام شود، تا بالای پله می‌روی؛ اگر روی سرِ یک مار تمام شود، تا دُمِ مار پایین می‌آیی.",
      "هر بار ۶ بیاوری یک بار دیگر تاس می‌ریزی — حتی اگر مهره نتواند حرکت کند.",
      "اگر در وقتِ نوبت کاری نکنی، تاس و حرکتت خودکار انجام می‌شود.",
    ],
    keyRules: [
      "برای شروع لازم نیست ۶ بیاوری: با همان تاسِ اول وارد صفحه می‌شوی.",
      "اگر تاس از خانهٔ ۱۰۰ رد شود، مهره حرکت نمی‌کند — باید دقیقاً به ۱۰۰ برسی.",
      "فقط کسی که دقیقاً روی خانهٔ ۱۰۰ بایستد برنده است.",
      "پله و مار فقط در خانه‌ای کار می‌کنند که حرکتت همان‌جا تمام شود؛ از روی خانه‌ای که فقط از آن رد می‌شوی، اتفاقی نمی‌افتد. هر حرکت حداکثر یک پله یا یک مار دارد.",
      "هر بار ۶ بیاوری یک بار دیگر تاس می‌ریزی — حتی اگر مهره نتواند حرکت کند. برای ۶های پشتِ‌سرهم سقفی نیست.",
      "اگر مهره‌ات — بعد از مار یا پله — در خانه‌ای بایستد که مهرهٔ حریفی آنجاست، مهرهٔ حریف زده می‌شود و به شروع برمی‌گردد. در خانهٔ ۱۰۰ کسی زده نمی‌شود.",
      "زدنِ مهره به‌تنهایی تاسِ اضافه نمی‌دهد و مهرهٔ زده‌شده با هر عددی دوباره از شروع راه می‌افتد.",
      "سه سطح دارد: آسان، متوسط و سخت — هر سطح نقشه‌های خودش را دارد. سطح را در بازی تک‌نفره و میز خصوصی خودت انتخاب می‌کنی؛ میز عمومی همیشه متوسط است.",
      "هر بازی یکی از نقشه‌های سطحش را به‌تصادف می‌گیرد: در سطحِ آسان پله‌ها بلندتر و مارها کمتر و کوتاه‌ترند و در سطحِ سخت مارها بلندتر و پله‌ها کمترند.",
      "سه ظاهر هم دارد: «جنگل» و «باغ ایرانی» با تصویرِ پس‌زمینه و «اورجینال»، صفحهٔ کلاسیکِ بدونِ تصویر؛ ظاهر در قوانینِ بازی اثری ندارد.",
      "ظاهر صفحه را در بازی تک‌نفره و میز خصوصی خودت انتخاب می‌کنی؛ میز عمومی یکی از تصویرها را تصادفی می‌گیرد.",
      "در میزِ آنلاین برای ریختنِ تاس زمان داری و بعد برای حرکتِ مهره هم همان‌قدر: میزِ عمومی ۱۰ ثانیه، و در میزِ خصوصی میزبان بینِ ۱۰، ۱۵، ۲۰ و ۲۵ ثانیه انتخاب می‌کند. بازیِ تک‌نفره وقتِ نوبت ندارد.",
      "در میزِ عمومی اگر پنج بار پشتِ‌سرِهم وقتت تمام شود، از میز کنار گذاشته می‌شوی و آن بازی برایت تمام می‌شود (در میزِ ۴ نفره رباتی جایت را می‌گیرد).",
    ],
    scoring: [
      "مار پله امتیازِ عددی ندارد: نخستین کسی که دقیقاً به خانهٔ ۱۰۰ برسد، همان لحظه برندهٔ بازی است و بازی تمام می‌شود؛ بقیهٔ بازیکن‌ها می‌بازند.",
      "ششی که مهره را به ۱۰۰ برساند تاسِ دیگری نمی‌دهد، چون بازی تمام شده است.",
    ],
  },
  {
    id: "backgammon",
    name: "تخته نرد",
    summary: "مسابقهٔ مهره و تاس؛ هر ۱۵ مهره‌ات را دور بزن و زودتر از حریف جمع کن.",
    goal: "تخته نرد مسابقه‌ای دونفره است: هر بازیکن ۱۵ مهره دارد و باید همهٔ آن‌ها را در جهتِ خودش دورِ صفحه بچرخاند، به شش‌خانهٔ پایانیِ خودش برساند و از صفحه بیرون («جمع») کند. هرکس زودتر هر ۱۵ مهره‌اش را جمع کند، برنده است.",
    players: "۲ نفر. هرکدام ۱۵ مهره دارید و در جهتِ مخالفِ هم حرکت می‌کنید.",
    howToPlay: [
      "چیدمانِ آغازین برای هر بازیکن: ۲ مهره روی خانهٔ ۲۴، ۵ مهره روی ۱۳، ۳ مهره روی ۸ و ۵ مهره روی ۶.",
      "برای تعیینِ شروع‌کننده هرکس یک تاس می‌اندازد؛ عددِ بزرگ‌تر شروع می‌کند و همان دو تاس را بازی می‌کند — تاسِ خودش عددِ اول و تاسِ حریف عددِ دوم. اگر مساوی شد، هر دو دوباره می‌اندازند.",
      "در هر نوبت دو تاس می‌اندازید و هر عدد را جداگانه بازی می‌کنید — با دو مهرهٔ مختلف، یا هر دو عدد با یک مهره.",
      "اگر جفت بیاورید، همان عدد را چهار بار بازی می‌کنید (نه دو بار).",
      "روی خانه‌ای می‌توانید بنشینید که خالی باشد، مالِ خودتان باشد، یا فقط یک مهرهٔ تنهای حریف روی آن باشد.",
      "مهرهٔ تنها در معرضِ خطر است: با نشستن روی مهرهٔ تنهای حریف آن را می‌زنید و آن مهره به تیغهٔ وسطِ صفحه («بار») می‌رود.",
      "مهرهٔ زده‌شده باید از سمتِ خانهٔ حریف دوباره وارد شود، و تا وارد نشده هیچ مهرهٔ دیگری از شما حق حرکت ندارد.",
      "وقتی هر ۱۵ مهره‌تان به شش‌خانهٔ پایانیِ خودتان رسید، جمع‌کردن آغاز می‌شود.",
      "هرکس زودتر هر ۱۵ مهره‌اش را جمع کند، آن دست را می‌برد و به‌اندازهٔ ارزشِ آن دست جلو می‌افتد.",
      "حرکت‌ها را روی صفحه می‌چینید و در پایانِ نوبت تأیید می‌کنید؛ تا پیش از تأیید می‌توانید حرکتِ آخر را پس بگیرید.",
    ],
    keyRules: [
      "باید بیشترین تعداد تاسی را که ممکن است بازی کنید؛ اگر فقط یکی از دو عدد قابلِ بازی باشد، عددِ بزرگ‌تر اولویت دارد.",
      "اگر هیچ حرکتِ قانونی نداشته باشید، نوبت بدونِ بازی به حریف می‌رسد.",
      "خانه‌ای که ۲ مهرهٔ حریف یا بیشتر روی آن است بسته است و نمی‌توانید رویش بنشینید.",
      "برای تعدادِ مهره‌های خودتان روی یک خانه محدودیتی نیست.",
      "«زدن و بلند شدن» در شش‌خانهٔ خودتان ممنوع است: اگر داخلِ شش‌خانهٔ پایانیِ خودتان مهرهٔ تنهای حریف را بزنید، همان مهرهٔ شما تا آخرِ آن نوبت دیگر حرکت نمی‌کند.",
      "این ممنوعیت دو استثنا دارد: اگر با تاسِ جفت زده باشید، و اگر هیچ حرکتِ دیگری نداشته باشید — در این دو حالت همان مهره می‌تواند حرکت کند.",
      "در جمع‌کردن، عددِ دقیق مهره را بیرون می‌برد؛ اگر عددِ تاس از بالاترین خانهٔ پرِ شما بزرگ‌تر باشد، از همان بالاترین خانه جمع می‌کنید.",
      "اگر وسطِ جمع‌کردن مهره‌تان زده شود، باید دوباره واردش کنید و تا شش‌خانهٔ خودتان برگردانید تا جمع‌کردن از سر گرفته شود.",
      "در این نسخه تاسِ دوبل (کوب) وجود ندارد.",
    ],
    scoring: [
      "بازی یک مسابقه است تا تعدادِ دستِ تعیین‌شدهٔ میز (۱، ۳، ۵ یا ۷ دست)؛ هر بُرد به‌اندازهٔ ارزشش دست حساب می‌شود و مسابقه وقتی تمام می‌شود که یکی به آن عدد برسد.",
      "بردِ ساده — ۱ دست: حریف دستِ‌کم یک مهره جمع کرده باشد.",
      "مارس — ۲ دست: هر ۱۵ مهره‌تان را جمع کنید و حریف هیچ مهره‌ای جمع نکرده باشد.",
      "سگ‌مارس — ۳ دست: هر ۱۵ مهره‌تان را جمع کنید، حریف هیچ مهره‌ای جمع نکرده باشد، و هنوز مهره‌ای در شش‌خانهٔ شما یا در بار داشته باشد. پس در مسابقهٔ ۳ دستی، یک سگ‌مارس در دستِ اول کلِ مسابقه را می‌برد.",
      "دست در همان لحظه‌ای تمام می‌شود که پانزدهمین مهره جمع شود.",
    ],
  },
  {
    id: "dooz",
    name: "دوز",
    summary: "دونفره روی صفحهٔ ۷×۶؛ مهره را در یک ستون بینداز و زودتر چهارتا را ردیف کن.",
    goal: "دوز بازی دونفرهٔ ساده‌ای است روی صفحه‌ای ایستاده با ۷ ستون و ۶ ردیف. به‌نوبت مهره می‌اندازید و هرکس زودتر چهار مهرهٔ هم‌رنگ را کنارِ هم بچیند — افقی، عمودی یا مورب — آن دست را می‌برد.",
    players: "۲ نفر، هرکدام با یک رنگ مهره.",
    howToPlay: [
      "برای اینکه معلوم شود چه کسی دستِ اول را شروع می‌کند یک تاس انداخته می‌شود؛ در دست‌های بعدی نوبتِ شروع بینِ دو نفر عوض می‌شود.",
      "در نوبتِ خودت انگشتت را روی هر ستونی نگه دار: یک مهرهٔ کم‌رنگ نشان می‌دهد مهره دقیقاً در کدام خانه می‌نشیند.",
      "انگشتت را می‌توانی روی ستون‌های دیگر بکشی؛ با برداشتنِ انگشت، مهره در همان ستون می‌افتد.",
      "مهره همیشه تا پایین‌ترین خانهٔ خالیِ آن ستون سقوط می‌کند — نمی‌توانی خانه را انتخاب کنی، فقط ستون را.",
      "هر ستونی که پر شده باشد دیگر قابلِ انتخاب نیست.",
      "به‌محضِ اینکه چهار مهرهٔ هم‌رنگ کنارِ هم ردیف شود، همان خط روشن می‌شود و آن دست تمام است.",
      "اگر همهٔ ۴۲ خانه پر شود و هیچ‌کس چهارتا ردیف نکرده باشد، آن دست مساوی است.",
      "پس از هر دست، صفحه پاک می‌شود و دستِ بعد با نوبتِ شروعِ عوض‌شده دوباره چیده می‌شود.",
    ],
    keyRules: [
      "چهار مهره در چهار جهت حساب می‌شود: افقی، عمودی، و هر دو مورب.",
      "مهره فقط از بالا انداخته می‌شود و جای خودش را با جاذبه پیدا می‌کند؛ هیچ مهره‌ای جابه‌جا یا برداشته نمی‌شود.",
      "در میزِ آنلاین برای هر نوبت زمان داری؛ اگر تمام شود سیستم به‌جایت بازی می‌کند، و پنج نوبتِ پشتِ‌سرهم که بازی نکنی جایت را به ربات می‌دهد.",
      "رنگِ مهره‌ها فقط برای خودت است: هرکس مهره‌های خودش را با رنگی که انتخاب کرده می‌بیند، و صفحه برای هر دو یکی است.",
    ],
    scoring: [
      "هر مسابقه تعدادِ مشخصی دست دارد: میزِ عمومی همیشه ۳ دست، و در میزِ خصوصی میزبان بینِ ۳ و ۵ انتخاب می‌کند.",
      "هر دستی که ببری یک امتیاز دارد؛ دستِ مساوی برای هیچ‌کدام امتیازی ندارد.",
      "همهٔ دست‌ها بازی می‌شوند، حتی اگر نتیجه زودتر معلوم شده باشد — چون نوبتِ شروع یک‌درمیان عوض می‌شود و برابری فقط وقتی کامل است که همهٔ دست‌ها بازی شوند.",
      "در پایان، هرکس دستِ بیشتری برده باشد برندهٔ مسابقه است؛ اگر برابر شوند، مسابقه مساوی می‌شود.",
    ],
  },
  /*
   * «مار» (the operator's order, 2026-11-05) — every value from the code prod runs: `SNAKE_*` in
   * `@bing/shared` and `game/snake` (it is played wholly on the phone, so it has no package here).
   * `apps/web/test/snake-guide.test.tsx` builds the points and the bonus's timing from those constants,
   * so a change to the scoring reddens it. Not a `GameId`, so not in `LOBBY_GAME_ORDER`: {@link GUIDE_ORDER}
   * puts it where the lobby has its tile.
   */
  {
    id: "snake",
    name: "مار",
    summary:
      "مارِ کلاسیکِ گوشی‌های قدیمی، برگشته و برای بازیکن‌های ما از نو طراحی شده؛ غذا بخور، بلندتر شو و به خودت نخور.",
    goal: "مار همان بازیِ کلاسیکِ گوشی‌های قدیمی است که برگشته و برای بازیکن‌های ما از نو طراحی شده. تک‌نفره است: تا می‌توانی غذا بخور، امتیاز جمع کن و رکوردت را بالا ببر. با هر غذا مار بلندتر می‌شود و جا تنگ‌تر؛ اگر مار کلِ صفحه را پر کند، برده‌ای.",
    players: "فقط ۱ نفر؛ نه حریف دارد، نه ربات و نه وقتِ نوبت.",
    howToPlay: [
      "در صفحهٔ شروع «سرعت» و «ظاهر» را انتخاب کن و «شروع» را بزن؛ بعد از شمارشِ ۳، ۲، ۱ مار از وسطِ صفحه رو به راست راه می‌افتد.",
      "مار خودش جلو می‌رود و تو فقط می‌چرخانی‌اش: با چهار دکمهٔ جهت‌دار، یا روی دسکتاپ با کلیدهای جهت‌دارِ صفحه‌کلید یا W A S D.",
      "هر غذا مار را ۲ خانه بلند می‌کند.",
      "بعد از هر ۵ غذا یک جایزه هم پیدا می‌شود: یک بلوکِ ۲×۲ که فقط تا ۴۰ خانهٔ بعدی می‌ماند و بعد ناپدید می‌شود. خوردنش مار را ۴ خانه بلند می‌کند.",
      "تا جایزه روی صفحه است، ارزشش با ★ کنارِ نامِ بازی دیده می‌شود.",
    ],
    keyRules: [
      "دیوار ندارد: از هر لبه که بیرون بروی، مار از لبهٔ روبه‌رو برمی‌گردد.",
      "مار فقط وقتی می‌میرد که به خودش بخورد.",
      "جهتِ برعکس کار نمی‌کند: مار نمی‌تواند یک‌باره دور بزند.",
      "فقط غذای معمولی برای جایزهٔ بعدی شمرده می‌شود.",
      "«سرعت» سه تا دارد: «آهسته»، «معمولی» و «سریع»؛ هرچه سریع‌تر، هر غذا امتیاز بیشتری دارد.",
      "«ظاهر» دو تا دارد: «امروزی» به رنگ‌های بازی‌های ما و «نوستالژی» با صفحهٔ سبز و پیکسل‌های تیرهٔ گوشی‌های قدیمی؛ ظاهر در بازی و امتیاز اثری ندارد.",
      "سرعت و ظاهر روی همین گوشی به یاد می‌مانند.",
      "اگر اپ را کوچک کنی یا به برنامهٔ دیگری بروی، بازی مکث می‌کند و با شمارشِ ۳، ۲، ۱ ادامه می‌یابد؛ اما بازی ذخیره نمی‌شود: با بستنِ اپ تمام است و فقط رکوردت می‌ماند.",
    ],
    scoring: [
      "«آهسته»: هر غذا ۱ امتیاز؛ جایزه تا ۲۰ امتیاز و حدود ۷ ثانیه.",
      "«معمولی»: هر غذا ۲ امتیاز؛ جایزه تا ۴۰ امتیاز و حدود ۵ ثانیه.",
      "«سریع»: هر غذا ۳ امتیاز؛ جایزه تا ۶۰ امتیاز و حدود ۴ ثانیه.",
      "ارزشِ جایزه با هر ۲ خانه‌ای که مار جلو می‌رود به‌اندازهٔ یک غذا کم می‌شود تا برسد به ارزشِ یک غذا، پس زودتر بخورش.",
      "هر سرعت رکوردِ جدای خودش را دارد: بهترین امتیازت در همان سرعت، که در صفحهٔ شروع دیده می‌شود و با گوشیِ تازه هم از دست نمی‌رود.",
    ],
  },
  /*
   * «سودوکو» (the operator's order, 2026-11-05) — every sentence from the code prod runs: the engine
   * (`@bing/engine-sudoku`), the screens (`game/sudoku`) and the api's `SudokuService`.
   * `apps/web/test/sudoku-guide.test.tsx` asks the engine in the same file. Not a `GameId`, so not in
   * `LOBBY_GAME_ORDER`: {@link GUIDE_ORDER} puts it last, where the lobby has its tile.
   */
  {
    id: "sudoku",
    name: "سودوکو",
    summary:
      "پازلِ عددیِ تک‌نفره؛ جدول را طوری پر کن که هیچ عددی در یک ردیف، یک ستون یا یک جعبه تکرار نشود.",
    goal: "سودوکو پازلی عددی است: باید خانه‌های خالیِ جدول را طوری پر کنی که در هر ردیف، هر ستون و هر جعبه، هر عدد دقیقاً یک بار بیاید. هر جدول فقط یک جواب دارد و بدونِ حدس‌زدن حل می‌شود. وقتی همهٔ خانه‌ها درست پر شوند، همان لحظه برده‌ای.",
    players: "فقط ۱ نفر؛ نه حریف دارد، نه ربات و نه وقتِ نوبت.",
    howToPlay: [
      "در صفحهٔ شروع، در کارتِ «بازی جدید» اندازه و سطح را انتخاب کن و «شروع» را بزن؛ یا کارتِ «سودوکوی روز» را بزن.",
      "روی یک خانه بزن تا انتخاب شود؛ بعد روی عدد بزن تا در آن بنشیند. عددی را که خودت گذاشته‌ای می‌توانی با عددِ دیگری عوض کنی.",
      "ردیف، ستون و جعبهٔ خانهٔ انتخاب‌شده و هر خانه‌ای که همان عدد را دارد مشخص می‌شوند.",
      "«یادداشت» را که روشن کنی، عددها کوچک داخلِ خانهٔ خالی یادداشت می‌شوند و با زدنِ دوباره پاک می‌شوند. عددی که در خانه بگذاری، یادداشت‌های همان خانه را پاک می‌کند.",
      "«پاک کن» عدد یا یادداشت‌های خانهٔ انتخاب‌شده را پاک می‌کند.",
      "«برگرد» آخرین حرکتت را — عدد، یادداشت یا پاک‌کردن — پس می‌گیرد و تا ۱۰۰ حرکت را به یاد دارد.",
      "«راهنمایی» خانه‌ای را با جوابش پر می‌کند؛ هر بازی ۳ تا دارد و عددِ روی دکمه می‌گوید چندتا مانده.",
      "بازی خودکار ذخیره می‌شود. بیرون که بروی، کارتِ «بازی نیمه‌تمام» در صفحهٔ شروع با «ادامه» بازش می‌کند، روی گوشیِ دیگر هم.",
    ],
    keyRules: [
      "دو اندازه دارد: ۹×۹ با عددهای ۱ تا ۹ و جعبه‌های ۳×۳، و ۶×۶ با عددهای ۱ تا ۶ و جعبه‌های ۲ ردیف در ۳ ستون.",
      "سه سطح دارد: آسان، متوسط و سخت — هرچه سطح بالاتر، جدول روش‌های سخت‌تری می‌خواهد و معمولاً با خانه‌های پرِ کمتری شروع می‌شود. هر «بازی جدید» جدولی تصادفی می‌گیرد.",
      "خانه‌هایی که از اول پر هستند قفل‌اند و عوض یا پاک نمی‌شوند.",
      "هر بار یک بازیِ نیمه‌تمام داری؛ بازیِ جدید، بعد از تأیید، جایش را می‌گیرد.",
      "«بررسی اشتباه»: این کلید در صفحهٔ شروع است، پیش‌فرض روشن است و روی بازیِ بعدی اثر می‌گذارد؛ هر بازی تا آخر با حالتِ خودش می‌ماند.",
      "بررسیِ روشن: عددِ اشتباه همان لحظه قرمز می‌شود و شمرده می‌شود، حتی اگر عددِ اشتباهی را با عددِ اشتباهِ دیگری عوض کنی. پاک‌کردن و «برگرد» از شمارش کم نمی‌کنند؛ یادداشت و راهنمایی اشتباه حساب نمی‌شوند. با اشتباهِ سوم بازی همان لحظه تمام است و می‌بازی.",
      "بررسیِ خاموش: چیزی درست و غلط را نشان نمی‌دهد و سقفی برای اشتباه نیست. اولین باری که جدول با اشتباه پر شود، فقط می‌گوید چند خانه اشتباه است، نه کجا. از آن پس «نشانم بده» یک بار در هر بازی خانه‌های اشتباه را قرمز می‌کند و هر خانه با عوض‌شدنِ عددش عادی می‌شود.",
      "راهنمایی: خانهٔ انتخاب‌شده را اگر خالی باشد (با بررسیِ روشن، اگر اشتباه هم باشد) با جوابش پر می‌کند؛ وگرنه خودش خانه‌ای را انتخاب می‌کند: با بررسیِ روشن اولین عددِ اشتباه، و بعد خانهٔ خالی‌ای که کمترین گزینه را دارد. با بررسیِ خاموش فقط خانهٔ خالی را پر می‌کند، پس لو نمی‌دهد چه عددی اشتباه است.",
      "خانهٔ راهنمایی‌شده مثلِ خانه‌های اولِ جدول قفل می‌شود؛ «برگرد» آن را پس نمی‌گیرد و راهنمایی پس داده نمی‌شود.",
      "«سودوکوی روز»: هر روز یک جدولِ ۹×۹ متوسط، برای همه یکسان، که نیمه‌شب به وقتِ تهران عوض می‌شود. جدا از «بازی نیمه‌تمام» ذخیره می‌شود و از کارتِ خودش ادامه می‌یابد. هر روز یک فرصت داری: اگر با سه اشتباه ببازی، تا فردا تمام است.",
    ],
    scoring: [
      "سودوکو امتیازِ عددی ندارد؛ چیزی که می‌ماند زمان است. ساعت فقط وقتی بازی روی صفحه است جلو می‌رود و محدودیتی ندارد.",
      "رکورد: بهترین زمانت در هر اندازه و سطح جداگانه نگه داشته می‌شود و در صفحهٔ شروع برای اندازه و سطحِ انتخاب‌شده دیده می‌شود.",
      "زمانِ «سودوکوی روز» هم برای رکوردِ ۹×۹ متوسط حساب می‌شود.",
      "بازیِ حل‌شده با راهنمایی یا «نشانم بده» هم رکورد حساب می‌شود؛ بازیِ باخته رکوردی ندارد.",
    ],
  },
];

/**
 * «مار» and «سودوکو» are not `GameId`s, so they are not in `LOBBY_GAME_ORDER`: a bare `indexOf` would give
 * them −1 and show them FIRST. `Lobby.tsx` appends their tiles after the grid, in this order; so does this.
 */
const GUIDE_ORDER: readonly GuideGameId[] = [...LOBBY_GAME_ORDER, "snake", "sudoku"];

/**
 * The guides, in the LOBBY's own order — DERIVED from `LOBBY_GAME_ORDER`, never a hand-kept copy:
 * the card games first (حکم، بی‌دل، شلم، پاسور، بلوف), then the board games (شطرنج، منچ، دوز،
 * تخته نرد، مار پله), «مار» and «سودوکو». A hand-kept order fell behind the lobby once already: «مار پله»
 * and «دوز» swapped places in the lobby (the operator, card-86 round 2) and this list kept the old one.
 */
export const GAME_GUIDES: readonly GameGuide[] = [...GUIDES].sort(
  (a, b) => GUIDE_ORDER.indexOf(a.id) - GUIDE_ORDER.indexOf(b.id),
);
